import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Play, CheckCircle, Sparkles } from 'lucide-react';
import { heroData } from '../../data/mock';

// Animated counter hook
const useCountUp = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * (end - start) + start));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration, start]);

  return { count, ref };
};

const StatCard = ({ value, suffix, label, delay }) => {
  const numericValue = typeof value === 'number' ? value : parseFloat(value) || 0;
  const hasDecimal = numericValue % 1 !== 0;
  const { count, ref } = useCountUp(numericValue, 2000);
  
  // Format display value - show decimal for values like 1.2
  const displayValue = hasDecimal ? numericValue.toFixed(1) : count;
  
  return (
    <div 
      ref={ref}
      className="text-center p-4 md:p-6 rounded-2xl transition-all duration-300 hover:scale-105"
      style={{ 
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(10px)',
        animationDelay: `${delay}ms`
      }}
    >
      <div className="display-medium mb-1" style={{ color: 'var(--brand-dark)' }}>
        {displayValue}{suffix}
      </div>
      <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
        {label}
      </div>
    </div>
  );
};

const HeroSection = ({ onOpenCalendar }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <section 
      className="relative min-h-screen flex items-center pt-28 pb-12 md:pt-32 md:pb-20 overflow-hidden"
      style={{ background: 'var(--bg-page)' }}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-20 -right-32 w-[500px] h-[500px] rounded-full opacity-40 blur-3xl animate-float"
          style={{ background: 'var(--brand-primary)' }}
        />
        <div 
          className="absolute -bottom-20 -left-32 w-[400px] h-[400px] rounded-full opacity-30 blur-3xl animate-float"
          style={{ background: 'var(--brand-primary)', animationDelay: '1s' }}
        />
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-10 blur-3xl"
          style={{ background: 'var(--brand-dark)' }}
        />
        <div className="absolute inset-0 bg-grid opacity-50" />
      </div>

      <div className="container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className={`${isLoaded ? 'animate-fadeInLeft' : 'opacity-0'}`}>
            {/* Badge */}
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 animate-pulse-glow"
              style={{ background: 'var(--brand-primary)', color: 'var(--brand-dark)' }}
            >
              <Sparkles size={16} />
              <span className="font-semibold text-sm">{heroData.tagline}</span>
            </div>

            {/* Headline */}
            <h1 className="display-hero mb-4" style={{ color: 'var(--text-primary)' }}>
              {heroData.headline}
            </h1>
            
            <p className="heading-2 mb-6" style={{ color: 'var(--brand-hover)' }}>
              {heroData.subheadline}
            </p>

            <p className="body-large mb-8 max-w-xl" style={{ color: 'var(--text-secondary)' }}>
              {heroData.description}
            </p>

            {/* Feature checklist */}
            <div className="flex flex-wrap gap-4 mb-8">
              {heroData.features.map((item, i) => (
                <div 
                  key={i}
                  className="flex items-center gap-2 text-sm font-medium"
                  style={{ color: 'var(--text-primary)' }}
                >
                  <CheckCircle size={18} style={{ color: 'var(--brand-hover)' }} />
                  {item}
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={onOpenCalendar}
                className="btn-accent text-base"
              >
                {heroData.ctaText}
                <ArrowRight size={20} />
              </button>
              <a
                href="#calculator"
                className="btn-secondary text-base"
              >
                <Play size={18} />
                {heroData.secondaryCtaText}
              </a>
            </div>
          </div>

          {/* Right Content - Stats Grid */}
          <div className={`${isLoaded ? 'animate-fadeInRight' : 'opacity-0'}`}>
            <div className="grid grid-cols-2 gap-4">
              {heroData.stats.map((stat, index) => (
                <StatCard
                  key={index}
                  value={stat.value}
                  suffix={stat.suffix}
                  label={stat.label}
                  delay={index * 100}
                />
              ))}
            </div>
            
            {/* Trust indicator */}
            <div 
              className="mt-6 p-4 rounded-2xl text-center"
              style={{ background: 'rgba(0, 69, 52, 0.05)' }}
            >
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Designed for <span className="font-semibold" style={{ color: 'var(--brand-dark)' }}>₹5 Cr+ Indian SMEs</span> ready for systematic growth
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
